import { findLast } from "@foretagsplatsen/core-helpers/array";
import Model from "../../../Model.js";
import periodReportEventFactory from "../periodReportEventFactory.js";

/**
 * `periodReportEventLog` represents a period report's lifecycle.
 *
 * By storing a list of events, it provides state, author, and timestamp data.
 *
 * @param {periodReportEvent[]} spec.events - list of events since the beginning of time
 */
export default class PeriodReportEventLog extends Model {
	constructor(spec) {
		super(...arguments);

		this._events = spec.events || [
			periodReportEventFactory.materialize({ type: "Create" }),
		];

		this._lastStatusChangeEvent = findLast(this._events, (event) =>
			event.isStatusEvent(),
		);
	}

	getEvents() {
		return [...this._events];
	}

	getViewEvents() {
		return this.getEvents().filter((event) => event.isView());
	}

	getAcknowledgeEvents() {
		return this.getEvents().filter((event) => event.isAcknowledged());
	}

	getViewByCompanyUserEvents() {
		return this.getEvents().filter((event) =>
			event.isViewedByCompanyUser(),
		);
	}

	getNonViewEvents() {
		return this.getEvents().filter((event) => !event.isView());
	}

	getLastEvent() {
		return this._events.at(-1);
	}

	getLastStatusChangeEvent() {
		return this._lastStatusChangeEvent;
	}

	getLastUpdateDate() {
		return this.getLastStatusChangeEvent().getTimestamp();
	}

	getPublishDate() {
		return this.isPublished() && this.getLastUpdateDate();
	}

	isPublished() {
		return this.getLastStatusChangeEvent().isPublish();
	}

	isWithdrawn() {
		return this.getLastStatusChangeEvent().isWithdraw();
	}

	isDraft() {
		return this.getLastStatusChangeEvent().isCreate();
	}

	isWithdrawable() {
		return this.isPublished();
	}

	isViewedByCompanyUser() {
		return this._events.some((event) => event.isViewedByCompanyUser());
	}

	isAcknowledged() {
		return this._events.some((event) => event.isAcknowledged());
	}

	hasBeenAcknowledgedBy(user) {
		return this._events.some((event) => event.isAcknowledgeBy(user));
	}

	static transformMaterializationData(data, context) {
		data.events = periodReportEventFactory.materializeAll(
			data.events,
			context,
		);
	}
}
